import React, {memo, useState} from 'react';

import Clipboard from 'clipboard';
import {useDispatch} from 'react-redux';

import {RenameListItem} from 'app/components/general/RenameListItem';
import {useCurrentUserState} from 'app/current-user/api';
import {ItemLatestActivity} from 'app/general/dashboards/ItemLastActivity';
import {DeleteDocumentModal} from 'app/general/modals/DeleteDocumentModal';
import {
  ActionsButton,
  type ActionsMenuItemsType,
} from 'app/general/widgets/ActionsButton';
import {useUpdateProduct} from 'app/market/api';
import {StyledDocItem} from 'app/procedures/styles';
import {useAccountSettingsState} from 'app/settings/api';
import {showNotification} from 'app/toasts/slice';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {ProductSerializedType} from 'sweet-types';

interface Props {
  product: ProductSerializedType;
}

export const TeamProductItem = memo<Props>(({product}: Props) => {
  const dispatch = useDispatch();
  const user = useCurrentUserState().data;
  const account = useAccountSettingsState().data!;

  const [isEditingName, setEditingName] = useState(false);
  const [isEditingDescription, setEditingDescription] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updateProduct] = useUpdateProduct();

  const actions: ActionsMenuItemsType = [
    {
      text: gt('Rename'),
      icon: 'fa fa-pen',
      key: 'rename',
      callback: () => {
        setEditingName(true);
      },
    },
    {
      text: gt('Edit Description'),
      icon: 'fa fa-pen',
      key: 'edit-description',
      callback: () => {
        setEditingDescription(true);
      },
    },
    {
      text: gt('Copy Link'),
      icon: 'far fa-cut',
      key: 'copy',
      callback: () => {
        Clipboard.copy(product.html_url);
        dispatch(showNotification(gt('Product link copied to clipboard')));
      },
    },
    {
      text: gt('Delete'),
      icon: 'far fa-trash',
      key: 'trash',
      callback: () => setShowDeleteModal(true),
    },
  ];
  if (product.is_public) {
    actions.push({
      text: gt('Make Private'),
      icon: 'fa fa-eye-slash',
      key: 'make-private',
      callback: () =>
        updateProduct({url: product.url, is_public: false}).unwrap(),
    });
  } else {
    actions.push({
      text: gt('Make Public'),
      icon: 'fa fa-globe-americas',
      key: 'make-public',
      disabled: !account.is_paying,
      callback: () =>
        updateProduct({url: product.url, is_public: true}).unwrap(),
    });
  }

  return (
    <StyledDocItem className="row border-top py-2 d-flex align-items-center">
      <div className="col-auto px-0">
        <i className="fa fa-fw fa-gift" />
      </div>

      <div className="col text-truncate text-nowrap d-flex align-items-center">
        {isEditingName ? (
          <RenameListItem
            initialValue={product.name}
            onSave={(name: string) =>
              updateProduct({url: product.url, name})
                .unwrap()
                .then(() => setEditingName(false))
            }
            onCancel={() => setEditingName(false)}
          />
        ) : product.is_public ? (
          <a
            href={product.html_url}
            className="text-dark font-weight-bold d-block d-sm-inline"
          >
            {product.name}
          </a>
        ) : (
          product.name
        )}

        {isEditingDescription ? (
          <RenameListItem
            initialValue={product.description || gt('Enter a description')}
            onSave={(description: string) =>
              updateProduct({url: product.url, description})
                .unwrap()
                .then(() => setEditingDescription(false))
            }
            onCancel={() => setEditingDescription(false)}
          />
        ) : (
          <span className="pl-2 text-muted font-weight-light font-italic">
            {product.description}
          </span>
        )}
      </div>

      <div className="col col-sm-6 col-lg-3 mr-auto order-lg-3">
        <ItemLatestActivity
          sortBy={SORT_BY_TYPES.CREATION_DATE}
          object={product}
        />
      </div>

      <div className="col-auto order-lg-4">
        {product.is_public ? <i className="fa fa-globe-americas" /> : null}
      </div>

      {user?.is_super_manager ? (
        <div className="col-auto pl-1 order-lg-5">
          <ActionsButton actions={actions} />
        </div>
      ) : null}

      {showDeleteModal != null ? (
        <DeleteDocumentModal
          show={showDeleteModal}
          object={{...product, content_type: 'product'}}
          redirectContentType={'product'}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => setShowDeleteModal(false)}
        />
      ) : null}
    </StyledDocItem>
  );
});
